<script setup lang="ts">
import { computed, ref } from 'vue'
import {
  validateIntroductionMessageContentForm,
  type IntroductionMessageContentFormType,
} from './formSchema'
import DolphinRadioButton from '@/components/atoms/DolphinRadioButton.vue'
import DolphinSelector from '@/components/atoms/DolphinSelector.vue'
import MessageAttachmentActions from '@/components/organisms/MessageAttachmentActions/MessageAttachmentActions.vue'
import CountingTextarea from '@/components/molecules/CountingTextarea.vue'

const props = defineProps<{
  scenarioChatOptions: { text: string; value: string }[]
  hasLineActivationProvider: boolean
}>()

const model = defineModel<IntroductionMessageContentFormType>({
  required: true,
})

const contentTypeOptions = [
  {
    label: 'シナリオチャット',
    type: 'scenarioChat',
    handler: () => {
      model.value = { type: 'scenarioChat', scenarioChatGuid: '' }
    },
    condition: props.hasLineActivationProvider,
  },
  {
    label: '質問メッセージ',
    type: 'plain',
    handler: () => {
      model.value = { type: 'plain', body: '', selections: [] }
    },
    condition: true,
  },
]

const hasMultipleOptions = computed(() => {
  return contentTypeOptions.filter(option => option.condition).length > 1
})

const errors = computed(() =>
  validateIntroductionMessageContentForm(model.value)
)

const invalid = ref<{
  scenarioChatGuid: boolean
  body: boolean
  selections: boolean
}>({
  scenarioChatGuid: false,
  body: false,
  selections: false,
})

const validateScenarioChatGuid = (): void => {
  invalid.value = {
    ...invalid.value,
    scenarioChatGuid: !!errors.value?.scenarioChatGuid,
  }
}

const validateBody = (): void => {
  invalid.value = {
    ...invalid.value,
    body: !!errors.value?.body,
  }
}

const validateSelections = (): void => {
  invalid.value = {
    ...invalid.value,
    selections: !!errors.value?.selections,
  }
}
</script>

<template>
  <div :class="$style.messageContentForm">
    <div v-if="hasMultipleOptions" :class="$style.contentType">
      <p :class="$style.title">メッセージタイプ</p>
      <div :class="[$style.contentType__buttons, $style.typeButtons]">
        <label
          v-for="option of contentTypeOptions"
          :key="option.type"
          :class="[$style.option, $style.typeButtons__option]"
        >
          <DolphinRadioButton
            name="introductionMessageContentType"
            :value="option.type"
            :modelValue="model.type"
            @update:modelValue="option.handler"
          />
          <span :class="$style.option__text">
            {{ option.label }}
          </span>
        </label>
      </div>
      <p :class="$style.notice">
        メッセージタイプを切り替えて保存すると、これまでに登録されていた質問メッセージは削除されます。ご注意ください。
      </p>
    </div>
    <div :class="$style.messageContentForm__content">
      <div v-if="model.type === 'scenarioChat'" :class="$style.scenarioChat">
        <p :class="$style.title">シナリオチャット選択</p>
        <DolphinSelector
          :class="$style.scenarioChat__selector"
          :options="props.scenarioChatOptions"
          v-model="model.scenarioChatGuid"
          :invalid="invalid.scenarioChatGuid"
          @blur="validateScenarioChatGuid"
        />
        <p v-if="errors?.scenarioChatGuid" :class="$style.scenarioChat__error">
          {{ errors.scenarioChatGuid }}
        </p>
      </div>
      <div v-else-if="model.type === 'plain'" :class="$style.plainMessage">
        <p :class="$style.title">メッセージ内容</p>
        <CountingTextarea
          :class="$style.plainMessage__body"
          placeholder="例）はじめまして！ご相談内容をチャットで教えてください。"
          :rows="8"
          v-model="model.body"
          :invalid="invalid.body"
          @blur="validateBody"
        />
        <p v-if="errors?.body" :class="$style.plainMessage__error">
          {{ errors.body }}
        </p>
        <p :class="$style.notice">
          メッセージは10文字以上で入力してください。<br />
          メッセージ中の<code>{{ '\{\{名前\}\}' }}</code
          >という文字列が、ユーザーの名前に置き換えられます。
        </p>
        <MessageAttachmentActions
          :class="$style.plainMessage__selections"
          v-model="model.selections"
          :shouldShowTimeSlotPickerButton="false"
          isDeleteAllSelectionsAllowed
          :invalid="invalid.selections"
          @blur="validateSelections"
        >
          <template #optionalMessage>
            選択肢は2つ以上、または0個にしてください。
          </template>
        </MessageAttachmentActions>
        <p v-if="errors?.selections" :class="$style.plainMessage__error">
          {{ errors.selections }}
        </p>
      </div>
    </div>
  </div>
</template>

<style lang="scss" module>
.contentType {
  &__buttons {
    &:nth-child(n + 2) {
      margin-top: 8px;
    }
  }
}

.typeButtons {
  &__option {
    &:nth-child(n + 2) {
      margin-left: 16px;
    }
  }
}

.messageContentForm {
  &__content {
    margin-top: 24px;
  }
}

.scenarioChat {
  &__selector {
    margin-top: 8px;
  }

  &__error {
    color: $color-caution-accent;
    font-size: 12px;
    font-weight: 700;

    &:nth-child(n + 2) {
      margin-top: 4px;
    }
  }
}

.plainMessage {
  &__body {
    margin-top: 8px;
  }

  &__notice {
    color: $color-text-and-object-medium-emphasis;
    font-size: 12px;

    &:nth-child(n + 2) {
      margin-top: 8px;
    }
  }

  &__selections {
    margin-top: 16px;
  }

  &__error {
    color: $color-caution-accent;
    font-size: 12px;
    font-weight: 700;

    &:nth-child(n + 2) {
      margin-top: 4px;
    }
  }
}

.title {
  font-size: 14px;
  font-weight: 700;
}

.option {
  display: inline-flex;
  align-items: center;

  &__text {
    font-size: 14px;

    &:nth-child(n + 2) {
      margin-left: 4px;
    }
  }
}

.notice {
  background-color: $color-surface-secondary;
  border-radius: $border-radius-base;
  color: $color-text-and-object-medium-emphasis;
  font-size: 12px;
  padding: 16px;
  margin-top: 16px;
}
</style>
